import React, { useState, useEffect } from 'react';
import {
  Card,
  TextContainer,
  Stack,
  Button,
  Heading,
  Badge,
  TextField,
  Banner,
  Collapsible,
  Icon,
  Text,
  Frame
} from '@shopify/polaris';
import { ChevronDownMinor, ChevronUpMinor, DuplicateMinor, ChevronRightMinor, ChevronLeftMinor } from '@shopify/polaris-icons';

/**
 * Persistent sidebar component for displaying AI-generated results
 * Can be minimized and restored as needed
 */
export const PersistentResultsSidebar = ({
  results = [],
  onEdit,
  onSave,
  visible = true,
  onToggleVisibility
}) => {
  const [expandedResults, setExpandedResults] = useState({});
  const [editedResults, setEditedResults] = useState({});
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Log component rendering
  console.log('PersistentResultsSidebar rendered with:', {
    resultsCount: results.length,
    visible,
    isMinimized,
    firstResult: results[0] ? 'Present' : 'Missing'
  });
  
  // Initialize edited results when new results come in
  useEffect(() => {
    if (results.length > 0) {
      const initialEdits = {};
      results.forEach(result => {
        if (result.status === 'success') {
          initialEdits[result.id] = { ...result.fields };
        }
      });
      setEditedResults(initialEdits);
    }
  }, [results]);
  
  // Toggle expansion of a result
  const toggleExpand = (resultId) => {
    setExpandedResults(prev => ({
      ...prev,
      [resultId]: !prev[resultId]
    }));
  };
  
  // Handle field edit
  const handleFieldEdit = (resultId, fieldName, value) => {
    setEditedResults(prev => ({
      ...prev,
      [resultId]: {
        ...prev[resultId],
        [fieldName]: value
      }
    }));
  };
  
  // Handle save all changes
  const handleSave = () => {
    if (onSave) {
      onSave(editedResults);
    }
  };
  
  // Toggle minimized state
  const toggleMinimized = () => {
    setIsMinimized(prev => !prev);
    // Also notify parent if needed
    if (onToggleVisibility) {
      onToggleVisibility(!isMinimized);
    }
  };
  
  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        console.log('Text copied to clipboard');
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };
  
  // If no results, don't render anything
  if (results.length === 0) {
    return null;
  }
  
  // Log visibility state
  console.log('PersistentResultsSidebar visibility check:', { visible, resultsCount: results.length, isMinimized });
  
  // Render minimized version (just a tab on the side)
  if (isMinimized) {
    return (
      <div 
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          cursor: 'pointer'
        }}
        onClick={toggleMinimized}
      >
        <div style={{
          backgroundColor: '#008060',
          color: 'white',
          padding: '12px 8px',
          borderRadius: '4px 0 0 4px',
          boxShadow: '0 0 10px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon source={ChevronLeftMinor} color="base" />
          <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', marginLeft: '4px' }}>
            Image Analyzer ({results.length})
          </div>
        </div>
      </div>
    );
  }
  
  // Render full sidebar
  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      width: '400px',
      backgroundColor: 'white',
      boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
      borderLeft: '1px solid #e0e0e0',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #dfe3e8',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f9fafb',
        borderTop: '3px solid #008060'
      }}>
        <Stack spacing="tight" alignment="center">
          <Heading>Image Analyzer Results</Heading>
          <Badge status="success">{results.length} Result{results.length !== 1 ? 's' : ''}</Badge>
        </Stack>
        <Button onClick={toggleMinimized} icon={ChevronRightMinor} monochrome>Minimize</Button>
      </div>
      
      {/* Results scrollable area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
      }}>
        {results.length === 0 ? (
          <Banner status="info" title="No Results">
            <p>No AI-generated results available.</p>
          </Banner>
        ) : (
          <Stack vertical spacing="loose">
            {results.map((result, index) => (
              <Card key={result.id || index} sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center" distribution="equalSpacing">
                    <Heading>{result.productName || `Result ${index + 1}`}</Heading>
                    <Badge status={result.status === 'success' ? 'success' : 'critical'}>
                      {result.status === 'success' ? 'Completed' : 'Error'}
                    </Badge>
                  </Stack>
                  
                  {result.imageUrl && (
                    <div style={{ textAlign: 'center', margin: '12px 0' }}>
                      <img 
                        src={result.imageUrl} 
                        alt="Analyzed product" 
                        style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }} 
                      />
                    </div>
                  )}
                  
                  {result.status === 'error' ? (
                    <Banner status="critical">
                      <p>{result.error || 'An unknown error occurred'}</p>
                    </Banner>
                  ) : (
                    <div>
                      <div 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleExpand(result.id)}
                      >
                        <Text variant="headingMd">Generated Content</Text>
                        <Button 
                          plain 
                          icon={expandedResults[result.id] ? ChevronUpMinor : ChevronDownMinor}
                        />
                      </div>
                      
                      <Collapsible
                        open={expandedResults[result.id] || false}
                        id={`result-${result.id}`}
                      >
                        <div style={{ marginTop: '8px' }}>
                          {Object.entries(result.fields || {}).map(([fieldName, value]) => (
                            <div key={fieldName} style={{ marginBottom: '16px' }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '4px'
                              }}>
                                <Text variant="headingSm">{fieldName}</Text>
                                <Button 
                                  plain 
                                  icon={DuplicateMinor} 
                                  onClick={() => copyToClipboard(editedResults[result.id]?.[fieldName] || value)}
                                  accessibilityLabel="Copy to clipboard"
                                />
                              </div>
                              
                              <TextField
                                value={editedResults[result.id]?.[fieldName] || value}
                                onChange={(newValue) => handleFieldEdit(result.id, fieldName, newValue)}
                                multiline={value && value.length > 100}
                                autoComplete="off"
                              />
                            </div>
                          ))}
                        </div>
                      </Collapsible>
                    </div>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </div>
    </div>
  );
};
